// NS3600:2018 lenker til Standard Online eller PDF-seksjon
// Denne filen mapper NS3600-referanser til innholdsforklaringer

export const NS3600_LENKER = {
  // Kapittel 6 - Bygningsdeler
  '6.1': {
    tittel: 'NS3600:2018 pkt 6.1 - Yttertak',
    innhold: `
**6.1 Yttertak**

Befaringen skal omfatte:
- Visuell kontroll av taktekking (materiale, alder, tilstand)
- Vurdering av underlag og bæresystem (der synlig)
- Kontroll av takrenner og nedløp
- Vurdering av pipegjenomføringer og takopplett
- Dokumentasjon av eventuelle skader

**Viktige punkter:**
- Observer tegn på lekkasjer eller fuktskader
- Dokumenter alder og type taktekking
- Vurder behov for utskiftning eller vedlikehold
    `.trim()
  },

  '6.2': {
    tittel: 'NS3600:2018 pkt 6.2 - Yttervegger',
    innhold: `
**6.2 Yttervegger**

Befaringen skal omfatte:
- Visuell kontroll av fasade (kledning, puss, mur)
- Vurdering av fuger og overganger
- Kontroll av vinduer og dører
- Vurdering av grunnmur og sokkel

**Viktige punkter:**
- Observer tegn på fuktskader eller råte
- Dokumenter sprekkdannelser
- Vurder vedlikeholdsbehov
    `.trim()
  },

  // Tabell A.1 - Våtrom
  'Tabell A.1': {
    tittel: 'NS3600:2018 Tabell A.1 - Våtrom',
    innhold: `
**Tabell A.1: Tilstandsgrader for våtrom**

**TG0 - Som nytt:**
- Membran dokumentert lagt etter forskriftene
- Ingen synlige skader eller slitasje
- Hulltaking viser ingen fukt
- Korrekt fall og høydeforskjell ved sluk

**TG1 - Meget god:**
- Membran sannsynligvis korrekt utført
- Ubetydelig slitasje på overflater
- Hulltaking viser ingen/liten fukt

**TG2 - God (vedlikeholdsetterslep):**
- Middels slitasje på overflater
- Fuger med sprekkdannelser
- Membran ukjent eller manglende dokumentasjon
- Vedlikehold nødvendig innen 2-5 år

**TG3 - Dårlig (tiltak nødvendig):**
- Membran defekt eller mangler
- Fuktskader påvist
- Omfattende sopp/mugg
- Utbedring nødvendig umiddelbart

**Obligatoriske kontroller (§2-18):**
- Hulltaking i tilstøtende vegger
- Kontroll av membran ved sluk
- Måling av høydeforskjell (min 25 mm)
- Kontroll av fall mot sluk
    `.trim()
  },

  'Tabell A.2': {
    tittel: 'NS3600:2018 Tabell A.2 - Kjøkken',
    innhold: `
**Tabell A.2: Tilstandsgrader for kjøkken**

Omfatter vurdering av:
- Benkeplate og skap
- Hvitevarer
- Ventilasjon/avtrekk
- Gulv og vegger
- Fukt rundt vask og komfyr
    `.trim()
  },

  'Tabell B.1': {
    tittel: 'NS3600:2018 Tabell B.1 - Bygningsdeler',
    innhold: `
**Tabell B.1: Tilstandsgrader for bygningsdeler**

Standard tilstandsgradvurdering for:
- Yttertak
- Yttervegger
- Vinduer og dører
- Fundament og sokkel
- Tekniske anlegg

**TG-skala:**
- TG0: Som nytt
- TG1: Meget god
- TG2: God (vedlikeholdsetterslep)
- TG3: Dårlig (tiltak nødvendig)
    `.trim()
  }
};

/**
 * Parser tekst og erstatter NS3600-referanser med klikkbare lenker
 * @param {string} tekst - Teksten som skal parses
 * @param {function} onLenkKlikk - Callback når lenke klikkes
 * @returns {Array} - Array av tekst-segmenter og lenke-komponenter
 */
export function parseNS3600Referanser(tekst, onLenkKlikk) {
  if (!tekst) return [tekst];

  const referanseMønster = /NS3600(?::2018)?\s+(?:pkt\s+)?(\d+\.\d+)|NS3600(?::2018)?\s+Tabell\s+([A-Z]\.\d+)/gi;
  const deler = [];
  let sistIndeks = 0;
  let match;

  while ((match = referanseMønster.exec(tekst)) !== null) {
    // Legg til tekst før matchen
    if (match.index > sistIndeks) {
      deler.push({ type: 'tekst', innhold: tekst.substring(sistIndeks, match.index) });
    }

    // Finn referansen (enten pkt 6.1 eller Tabell A.1)
    const referanse = match[1] || `Tabell ${match[2]}`;

    // Legg til lenke
    deler.push({
      type: 'lenke',
      innhold: match[0],
      referanse: referanse,
      onClick: () => onLenkKlikk(referanse)
    });

    sistIndeks = match.index + match[0].length;
  }

  // Legg til eventuell gjenværende tekst
  if (sistIndeks < tekst.length) {
    deler.push({ type: 'tekst', innhold: tekst.substring(sistIndeks) });
  }

  return deler.length > 0 ? deler : [{ type: 'tekst', innhold: tekst }];
}

/**
 * Hent informasjon om en NS3600-referanse
 * @param {string} referanse - Referanse (f.eks. "6.1" eller "Tabell A.1")
 * @returns {Object|null} - Informasjonsobjekt eller null hvis ikke funnet
 */
export function hentNS3600Info(referanse) {
  return NS3600_LENKER[referanse] || null;
}
